import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FronteggAuthService } from '@frontegg/angular';
import { Subscription, filter, take } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { ToastService } from '../../../services/toast-messages/toast-messages.service';
import { EtriveConnectService } from '../../../services/etrive-connect/etrive-connect.service';
import { ItpIntegrationSourceService } from '../../../services/itp-integration/sources/itp-integration-source.service';
import { ITPIntegrationCredentialsService } from '../../../services/itp-integration/credentials/itp-integration-credential.service';
import {
  ITPIntegrationCredentials,
  ITPIntegrationSourceCreate
} from '@softdocs/capture-ui-libraries';
import { Bind } from 'primeng/bind';
import { Divider } from 'primeng/divider';
import { ProgressSpinner } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Card } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { Button } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Source option for dropdown selection
 */
interface SourceOption {
  label: string;
  value: string;
}

/**
 * Integration step definition
 */
interface IntegrationStep {
  id: string;
  step: string;
  source: string | null;
  sourceId: string | null;
  desc: string;
}

/**
 * Component for managing ITP integration settings including credentials and source mappings.
 * Allows administrators to configure client credentials and map integration steps to sources.
 */
@Component({
  selector: 'app-itp-management',
  templateUrl: './itp-management.component.html',
  styleUrls: ['./itp-management.component.scss'],
  imports: [
    Bind,
    Divider,
    ProgressSpinner,
    FormsModule,
    InputText,
    Card,
    SelectModule,
    Button,
    TranslatePipe
  ]
})
export class ItpManagementComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private fronteggAuthService = inject(FronteggAuthService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private credentialsService = inject(ITPIntegrationCredentialsService);
  private etrieveConnectService = inject(EtriveConnectService);
  private itpIntegrationSourceService = inject(ItpIntegrationSourceService);

  private authSubscription?: Subscription;

  private readonly GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private readonly fixedSteps: ReadonlyArray<{ id: string; step: string; desc: string }> = [
    {
      id: '4F34E51A-D229-4295-9EA7-9A66D8D07D64',
      step: 'transcriptData',
      desc: 'transcriptDataDesc'
    },
    {
      id: '43ADAC41-C5AF-43E5-87F1-0521BB04B8B3',
      step: 'requiredFields',
      desc: 'requiredFieldsStepDesc'
    }
  ];

  user?: any;
  itpAdminRole: boolean = false;

  clientId: string = '';
  secret: string = '';
  clientIdTouched = signal(false);

  integrationSteps = signal<IntegrationStep[]>([]);
  sourceOptions = signal<SourceOption[]>([]);

  isLoading = signal(false);
  isSaving = signal(false);

  hasItpAdminRole = computed(() => {
    return !!this.user?.roles?.some((role: any) => role.key === 'ITP Admin');
  });

  /**
   * Initializes the component by subscribing to authentication state.
   * Waits until user is authenticated AND token is available before loading data.
   * Redirects unauthorized users.
   */
  ngOnInit(): void {
    this.authSubscription = this.fronteggAuthService.authState$
      .pipe(
        filter(authState => {
          return authState.isAuthenticated && !!authState.user && !!authState.user.accessToken;
        }),
        take(1)
      )
      .subscribe(
        authState => {
          this.user = authState.user;
          this.itpAdminRole = !!this.user?.roles?.some((role: any) => role.key === 'ITP Admin');

          if (!this.itpAdminRole) {
            this.router.navigate(['unauthorized']);
            return;
          }

          this.authService.setAuthToken(authState.user!.accessToken);
          this.loadData();
        },
        () => {
          this.router.navigate(['unauthorized']);
        }
      );
  }

  /**
   * Cleans up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  /**
   * Validates whether a given string is a valid GUID/UUID format.
   * @param value - The string to validate
   * @returns True if the value matches GUID format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
   */
  isValidGuid(value: string): boolean {
    return this.GUID_REGEX.test(value.trim());
  }

  /**
   * Marks the clientId field as touched for validation display purposes.
   */
  onClientIdBlur(): void {
    this.clientIdTouched.set(true);
  }

  /**
   * Loads all required data including credentials, source options,
   * and existing source mappings. Uses Promise.allSettled to handle partial failures gracefully.
   */
  private async loadData(): Promise<void> {
    this.isLoading.set(true);

    try {
      const [credentialsResult, sourcesResult, existingSourcesResult] = await Promise.allSettled([
        this.credentialsService.getITPIntegrationCredentials(),
        this.etrieveConnectService.getEtrieveSource(),
        this.itpIntegrationSourceService.getITPIntegrationSource()
      ]);

      if (credentialsResult.status === 'fulfilled' && credentialsResult.value) {
        this.clientId = credentialsResult.value.clientId || '';
        this.secret = '';
      }

      if (sourcesResult.status === 'fulfilled') {
        const sources = sourcesResult.value || [];
        this.sourceOptions.set(
          sources.map((s: any) => ({
            label: s.connectSourceName,
            value: s.connectSourceCode
          }))
        );
      } else {
        this.sourceOptions.set([]);
      }

      const existingSourcesByStepId = new Map<string, { id: string; code: string }>();
      if (existingSourcesResult.status === 'fulfilled') {
        const existingSources = existingSourcesResult.value || [];
        existingSources.forEach((source: any) => {
          const stepId = (source.integrationStepId || source.stepId || '').toUpperCase();
          existingSourcesByStepId.set(stepId, {
            id: source.id,
            code: source.code
          });
        });
      }

      const enrichedSteps: IntegrationStep[] = this.fixedSteps.map(step => {
        const existingSource = existingSourcesByStepId.get(step.id.toUpperCase());
        return {
          ...step,
          source: existingSource?.code || null,
          sourceId: existingSource?.id || null
        };
      });

      this.integrationSteps.set(enrichedSteps);
    } catch (error) {
      this.toastService.error('error', 'itpManagement.loadFailed');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Reloads sources after credentials have been saved.
   * Shows an error message if sources fail to load, indicating credentials may be invalid.
   */
  private async reloadSourcesAfterCredentialsSave(): Promise<void> {
    try {
      const sources = await this.etrieveConnectService.getEtrieveSource();
      this.sourceOptions.set(
        (sources || []).map((s: any) => ({
          label: s.connectSourceName,
          value: s.connectSourceCode
        }))
      );
    } catch (error) {
      this.sourceOptions.set([]);
      this.toastService.error('error', 'itpManagement.invalidCredentials');
    }
  }

  /**
   * Handles source selection changes for an integration step.
   * @param step - The integration step being modified
   * @param newSource - The newly selected source code
   */
  onSourceChange(step: IntegrationStep, newSource: string): void {
    this.integrationSteps.update(steps =>
      steps.map(s => (s.id === step.id ? { ...s, source: newSource } : s))
    );
  }

  /**
   * Generates a translation key for a step's description.
   * @param step - The step name to convert
   * @returns The translation key in format 'itpManagement.{step}'
   */
  getStepKey(step: string): string {
    return `itpManagement.${step}`;
  }

  /**
   * Retrieves the display label for a source code.
   * @param code - The source code to look up
   * @returns The source label if found, otherwise returns the code itself
   */
  private getSourceLabel(code: string): string {
    const option = this.sourceOptions().find(o => o.value === code);
    return option?.label || code;
  }

  /**
   * Saves all settings including credentials and source mappings.
   * Validates that clientId is a valid GUID before proceeding.
   * After saving credentials, reloads data to refresh available sources.
   */
  async saveAll(): Promise<void> {
    this.clientIdTouched.set(true);

    if (!this.clientId.trim()) {
      this.toastService.error('error', 'itpManagement.clientIdRequired');
      return;
    }

    if (!this.isValidGuid(this.clientId)) {
      this.toastService.error('error', 'itpManagement.clientIdInvalidGuid');
      return;
    }

    this.isSaving.set(true);

    try {
      const credentials: ITPIntegrationCredentials = {
        clientId: this.clientId.trim(),
        secret: this.secret
      };
      await this.credentialsService.manageCredentials(credentials);
      this.secret = '';

      const stepsWithSource = this.integrationSteps().filter(s => s.source);

      if (stepsWithSource.length > 0) {
        const savePromises = stepsWithSource.map(step => {
          const dto: ITPIntegrationSourceCreate = {
            name: this.getSourceLabel(step.source!),
            integrationStepId: step.id,
            code: step.source!,
            actionType: 1
          };

          if (step.sourceId) {
            return this.itpIntegrationSourceService
              .updateITPIntegrationSource(step.sourceId, dto)
              .then(() => ({ stepId: step.id, sourceId: step.sourceId }));
          } else {
            return this.itpIntegrationSourceService
              .addITPIntegrationSource(dto)
              .then(result => ({ stepId: step.id, sourceId: (result as any).id || null }));
          }
        });

        const results = await Promise.all(savePromises);

        results.forEach(({ stepId, sourceId }) => {
          if (sourceId) {
            this.integrationSteps.update(steps =>
              steps.map(s => (s.id === stepId ? { ...s, sourceId } : s))
            );
          }
        });
      }

      this.toastService.success('success', 'itpManagement.saveSuccess');
      await this.reloadSourcesAfterCredentialsSave();
    } catch (error) {
      this.toastService.error('error', 'itpManagement.saveFailed');
    } finally {
      this.isSaving.set(false);
    }
  }
}
